import { useEffect, useState } from "react";
import { addDoc, collection, doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import {
  FIRESTORE_PRODUCT_REJECT_REASONS_DOC,
  FIRESTORE_PRODUCT_REJECTS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const DEFAULT_REASONS = [
  { value: "weight_reject", label: "Weight reject" },
  { value: "metal_reject", label: "Metal reject" },
  { value: "bad_sealing", label: "Bad sealing" },
  { value: "plant_in_seal", label: "Plant in seal" },
];

export const labelToValue = (label) =>
  String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const rejectsColRef = () =>
  collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCT_REJECTS_TABLE);

const reasonsDocRef = () =>
  doc(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_PRODUCT_REJECTS_TABLE,
    FIRESTORE_PRODUCT_REJECT_REASONS_DOC,
  );

export const useProductRejects = () => {
  const { t } = useTranslation();
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      reasonsDocRef(),
      (snap) => {
        setReasons(snap.exists() ? snap.data()?.reasons || [] : DEFAULT_REASONS);
        setReasonsLoading(false);
      },
      (err) => {
        console.error("Error loading reject reasons:", err);
        message.error(t("crate_stock.reject_reasons_load_error"));
        setReasons(DEFAULT_REASONS);
        setReasonsLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);

  const saveReasons = async (updatedReasons) => {
    try {
      await setDoc(reasonsDocRef(), { reasons: updatedReasons }, { merge: true });
    } catch (err) {
      console.error("Error saving reject reasons:", err);
      message.error(t("crate_stock.reject_reasons_save_error"));
      throw err;
    }
  };

  const addReason = async ({ value, label }) => {
    if (!value || !label) return;
    await saveReasons([...reasons, { value, label }]);
    message.success(t("crate_stock.reason_added"));
  };

  const deleteReason = async (index) => {
    await saveReasons(reasons.filter((_, i) => i !== index));
    message.success(t("crate_stock.reason_deleted"));
  };

  const initDefaultsIfMissing = async () => {
    try {
      const snap = await getDoc(reasonsDocRef());
      if (!snap.exists()) {
        await setDoc(reasonsDocRef(), { reasons: DEFAULT_REASONS });
      }
    } catch (err) {
      console.error("Error initialising reject reasons defaults:", err);
    }
  };

  const addRejectEvent = async ({ productId, productName, quantity, reason, reasonLabel, uid, email, userName }) => {
    await addDoc(rejectsColRef(), {
      productId,
      productName,
      quantity,
      reason,
      reasonLabel,
      date: dayjs().format("DD-MM-YYYY"),
      dateUnix: dayjs().startOf("day").unix(),
      createdAt: dayjs().unix(),
      createdByUid: uid || null,
      createdBy: email || null,
      createdByName: userName || null,
    });
  };

  return { reasons, reasonsLoading, addRejectEvent, addReason, deleteReason, initDefaultsIfMissing };
};
